import { api } from "../../../../services/api.js";
import React, { useState, useRef, useEffect } from "react";
import { BUTTON_RESET, CARD, SURFACE_BORDER } from "../../../../styles";
const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const highlightMatch = (text, tokens) => {
  const source = String(text || "");
  const validTokens = (tokens || []).filter(
    (token) => token && token.length >= 2,
  );
  if (!source || validTokens.length === 0) {
    return source;
  }
  const escaped = validTokens
    .map((token) => escapeRegExp(token))
    .sort((left, right) => right.length - left.length);
  if (!escaped.length) {
    return source;
  }
  const regex = new RegExp(`(${escaped.join("|")})`, "ig");
  const parts = source.split(regex);
  return parts.map((part, index) => {
    const matched = validTokens.some(
      (token) => part.toLowerCase() === token.toLowerCase(),
    );
    if (!matched) {
      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    }
    return (
      <span
        key={`${part}-${index}`}
        style={{
          background: "#FFF4CC",
          color: "#7A4B00",
          borderRadius: 4,
          padding: "0 2px",
        }}
      >
        {part}
      </span>
    );
  });
};
const hasTextBlock = (aula) =>
  Array.isArray(aula?.guias) &&
  aula.guias.some(
    (guia) =>
      Array.isArray(guia?.blocos) &&
      guia.blocos.some((bloco) => bloco.tipo === "texto"),
  );
const hasAttachmentBlock = (aula) =>
  Array.isArray(aula?.guias) &&
  aula.guias.some(
    (guia) =>
      Array.isArray(guia?.blocos) &&
      guia.blocos.some(
        (bloco) =>
          bloco.tipo === "anexo" &&
          Array.isArray(bloco.arquivos) &&
          bloco.arquivos.length > 0,
      ),
  );
export function SearchClass(props) {
  const onOpen = props.onOpen;
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [result, setResult] = useState({
    total: 0,
    items: [],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("titles");
  const root = useRef(null);
  const request = useRef(0);
  const controller = useRef(null);
  const cache = useRef(new Map());
  const terms = submittedQuery.trim().split(/\s+/).filter(Boolean);
  useEffect(() => {
    const outside = (event) => {
      if (root.current && !root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside, true);
    return () => {
      document.removeEventListener("pointerdown", outside, true);
      controller.current?.abort();
      request.current++;
    };
  }, []);
  const reopen = () => {
    if (submittedQuery && query.trim() === submittedQuery) setOpen(true);
  };
  const handleSearch = async () => {
    const queryNormalizada = query.trim();
    if (queryNormalizada.length < 2) return;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const current = ++request.current;
    setSubmittedQuery(queryNormalizada);
    setOpen(true);
    setError("");
    const key =
      (props.tipo || "conteudo") +
      "|" +
      queryNormalizada.toLocaleLowerCase("pt-BR");
    const saved = cache.current.get(key);
    if (saved && Date.now() - saved.at < 30000) {
      setResult(saved.value);
      setBusy(false);
      return;
    }
    setBusy(true);
    setPhase("titles");
    setResult({
      total: 0,
      items: [],
    });
    try {
      const titles = await api.get("/conteudos/pesquisa", {
        params: {
          q: queryNormalizada,
          tipo: props.tipo || "conteudo",
          fase: "titulos",
        },
        signal: abort.signal,
      });
      if (current !== request.current) return;
      setResult(
        titles?.data?.data || {
          total: 0,
          items: [],
        },
      );
      setPhase("texts");
      const response = await api.get("/conteudos/pesquisa", {
        params: {
          q: queryNormalizada,
          tipo: props.tipo || "conteudo",
          fase: "todos",
        },
        signal: abort.signal,
      });
      if (current !== request.current) return;
      const value = response?.data?.data || {
        total: 0,
        items: [],
      };
      setResult(value);
      cache.current.set(key, {
        at: Date.now(),
        value,
      });
      if (cache.current.size > 12)
        cache.current.delete(cache.current.keys().next().value);
    } catch (err) {
      if (current === request.current && !abort.signal.aborted)
        setError(
          "Não foi possível concluir a pesquisa. Os resultados já encontrados continuam disponíveis. Tente novamente.",
        );
    } finally {
      if (current === request.current) setBusy(false);
    }
  };
  const caption = busy
    ? phase === "titles"
      ? "Buscando títulos…"
      : "Buscando nos textos das aulas…"
    : error || `${result.total} resultado(s) para “${submittedQuery}”`;
  return (
    <div
      ref={root}
      style={{
        position: "relative",
        marginBottom: 16,
      }}
    >
      <style>
        {
          '@keyframes dm-search-spin{to{transform:rotate(360deg)}}@keyframes dm-search-sweep{0%{transform:translateX(-110%)}100%{transform:translateX(340%)}}.dm-search-spinner{display:inline-block;width:13px;height:13px;border:2px solid rgba(79,179,169,.25);border-top-color:#4FB3A9;border-radius:50%;animation:dm-search-spin .8s linear infinite;flex-shrink:0}.dm-search-progress{height:3px;background:#E6F2F0;overflow:hidden}.dm-search-progress:after{content:"";display:block;height:100%;width:30%;background:#4FB3A9;animation:dm-search-sweep 1.2s ease-in-out infinite}@media(prefers-reduced-motion:reduce){.dm-search-spinner,.dm-search-progress:after{animation:none}.dm-search-progress:after{width:100%}}'
        }
      </style>
      <div
        onClick={reopen}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "rgba(255,255,255,.08)",
          border: "1px solid " + SURFACE_BORDER,
          borderRadius: 14,
          padding: "11px 12px",
        }}
      >
        <svg
          width={15}
          height={15}
          viewBox={"0 0 24 24"}
          fill={"none"}
          stroke={"rgba(255,255,255,.75)"}
          strokeWidth={2}
          aria-hidden={true}
        >
          <circle cx={11} cy={11} r={8} />
          <line x1={21} y1={21} x2={16.65} y2={16.65} />
        </svg>
        <input
          value={query}
          aria-label={"Pesquisar conteúdos"}
          aria-expanded={isOpen}
          aria-controls={"dm-search-results"}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(false);
            request.current++;
            controller.current?.abort();
            setBusy(false);
            setError("");
          }}
          onFocus={reopen}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSearch();
            }
            if (event.key === "Escape") setOpen(false);
          }}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#FFF",
            fontSize: 14,
            fontFamily: "inherit",
          }}
          role="combobox"
          aria-autocomplete="list"
        />
        <button
          onClick={handleSearch}
          disabled={query.trim().length < 2 || busy}
          style={{
            ...BUTTON_RESET,
            display: "flex",
            alignItems: "center",
            gap: 7,
            background:
              query.trim().length >= 2 ? "#4FB3A9" : "rgba(255,255,255,.18)",
            color: "#FFF",
            borderRadius: 10,
            padding: "7px 12px",
            fontSize: 12,
            fontWeight: 800,
            flexShrink: 0,
            cursor: query.trim().length >= 2 ? "pointer" : "default",
          }}
        >
          {busy && (
            <span
              className={"dm-search-spinner"}
              style={{
                borderTopColor: "#FFF",
              }}
              aria-hidden={true}
            />
          )}
          {busy ? "Buscando…" : "Pesquisar"}
        </button>
      </div>
      {isOpen && submittedQuery && (
        <div
          id={"dm-search-results"}
          role={"region"}
          aria-label={"Resultados da pesquisa"}
          aria-busy={busy}
          style={{
            ...CARD,
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 8px)",
            zIndex: 30,
            overflow: "hidden",
          }}
        >
          <div
            role={"status"}
            aria-live={"polite"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              fontSize: 12,
              color: "#526777",
              borderBottom: "1px solid #EEF3F6",
              background: "#F8FBFC",
            }}
          >
            {busy && (
              <span className={"dm-search-spinner"} aria-hidden={true} />
            )}
            {caption}
          </div>
          {busy && <div className={"dm-search-progress"} aria-hidden={true} />}
          {!busy && !error && !result.items.length && (
            <div
              style={{
                padding: 18,
                color: "#64748B",
                fontSize: 13,
              }}
            >
              {"Nenhum conteúdo encontrado."}
            </div>
          )}
          <div
            style={{
              maxHeight: "min(65vh,600px)",
              overflowY: "auto",
            }}
          >
            {result.items.map((item) => (
              <button
                onClick={() => {
                  onOpen(item.curso.id, item.aula.id);
                  setOpen(false);
                }}
                style={{
                  ...BUTTON_RESET,
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  background: "#FFF",
                  padding: "12px 14px",
                  borderBottom: "1px solid #EEF3F6",
                }}
                key={item.aula.id}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#18212B",
                  }}
                >
                  {highlightMatch(item.aula.titulo, terms)}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#64748B",
                    marginTop: 3,
                  }}
                >
                  {[
                    item.categoria.nome,
                    item.curso.nome,
                    item.modulo.nome,
                  ].join(" > ")}
                </div>
                {item.snippet && (
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: 1.6,
                      color: "#334155",
                      marginTop: 7,
                    }}
                  >
                    <span
                      style={{
                        color: "#0F766E",
                        fontWeight: 700,
                      }}
                    >{`Texto da aula · ${item.guia}: `}</span>
                    {highlightMatch(item.snippet, terms)}
                  </div>
                )}
              </button>
            ))}
          </div>
          {!busy && result.total > 50 && (
            <div
              style={{
                padding: 10,
                fontSize: 11,
                color: "#64748B",
              }}
            >
              {
                "Mostrando os primeiros 50 resultados. Use um termo mais específico para refinar a busca."
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
}
